import { Box, ButtonBase } from '@mui/material';
import type { ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';

export interface PanelTabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional leading element (e.g. a status dot) */
  icon?: ReactNode;
}

interface PanelTabsProps<T extends string> {
  tabs: PanelTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Stretch tabs to equal widths across the row (Figma panel tabs) */
  fullWidth?: boolean;
  className?: string;
}

/**
 * Figma section-header tabs: 32px row, IBM Plex Sans 13; active tab gets a
 * 2px purple underline, tabs are separated by a 1px divider.
 */
export function PanelTabs<T extends string>({
  tabs,
  value,
  onChange,
  fullWidth = false,
  className,
}: PanelTabsProps<T>) {
  return (
    <Box
      className={`panel-tabs${className ? ' ' + className : ''}`}
      sx={{ display: 'flex', alignItems: 'stretch', height: 32, width: '100%' }}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <ButtonBase
            key={tab.value}
            onClick={() => onChange(tab.value)}
            className={`panel-tab panel-tab-${tab.value}${active ? ' is-active' : ''}`}
            sx={{
              boxSizing: 'border-box',
              justifyContent: 'center',
              gap: 1.25,
              height: 32,
              px: 2,
              ...(fullWidth && { flex: 1, minWidth: 0 }),
              fontFamily: osirisTokens.fonts.body,
              fontSize: 13,
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              color: active ? osirisTokens.text.primary : osirisTokens.text.tertiary,
              borderBottom: active
                ? `2px solid ${osirisTokens.border.active}`
                : `1px solid ${osirisTokens.border.primary}`,
              '&:not(:first-of-type)': {
                borderLeft: `1px solid ${osirisTokens.border.primary}`,
              },
              '&:hover': { color: osirisTokens.text.primary },
            }}
          >
            {tab.icon}
            {tab.label}
          </ButtonBase>
        );
      })}
      {!fullWidth && (
        <Box sx={{ flex: 1, borderBottom: `1px solid ${osirisTokens.border.primary}` }} />
      )}
    </Box>
  );
}
